import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Badge, Button, Modal, Form, Alert, Dropdown, Spinner, Row, Col } from 'react-bootstrap';
import { 
    Loader2, 
    Briefcase, 
    RefreshCcw, 
    AlertTriangle, 
    Inbox, 
    MapPin, 
    MoreVertical, 
    Eye, 
    Edit3, 
    X,
    CheckCircle2,
    Info,
    FileText,
    Upload,
    Trash2,
    XCircle
} from 'lucide-react';
import { 
    getMyApplications, 
    getRecruiterApplications, 
    updateApplicationStatus, 
    getNextPossibleStatuses,
    withdrawApplication,
    deleteApplication
} from '../../services/applicationService';
import { getStatusIcon, getStatusColor, getStatusLabel } from '../../utils/statusHelpers';
import { formatDate as sharedFormatDate } from '../../utils/dateUtils';

const ApplicationStatusManager = ({ userType, userId }) => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [recruiterNotes, setRecruiterNotes] = useState('');
    const [updating, setUpdating] = useState(false);
    
    // Resume preview state variables
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewType, setPreviewType] = useState(''); // 'pdf' | 'image' | 'word' | 'other'
    const [previewLoading, setPreviewLoading] = useState(false);

    // Withdraw modal states
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [applicationToWithdraw, setApplicationToWithdraw] = useState(null);
    const [withdrawing, setWithdrawing] = useState(false);

    // Delete modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [applicationToDelete, setApplicationToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const handleWithdrawApplication = async () => {
        if (!applicationToWithdraw) return;
        try {
            setWithdrawing(true);
            const response = await withdrawApplication(applicationToWithdraw.id);
            if (response && (response.success || response.id)) {
                setApplications(prev => prev.map(app => 
                    app.id === applicationToWithdraw.id 
                        ? { ...app, status: 'WITHDRAWN' }
                        : app
                ));
                setShowWithdrawModal(false);
                setApplicationToWithdraw(null);
            } else {
                setError('Failed to withdraw application');
            }
        } catch (err) {
            console.error('Error withdrawing application:', err);
            setError('Failed to withdraw application. Please try again.');
        } finally {
            setWithdrawing(false);
        }
    };

    const handleDeleteApplication = async () => {
        if (!applicationToDelete) return;
        try {
            setDeleting(true);
            const response = await deleteApplication(applicationToDelete.id);
            if (response && response.success) {
                setApplications(prev => prev.filter(app => app.id !== applicationToDelete.id));
                setShowDeleteModal(false);
                setApplicationToDelete(null);
            } else {
                setError('Failed to delete application');
            }
        } catch (err) {
            console.error('Error deleting application:', err);
            setError('Failed to delete application. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const loadApplications = useCallback(async () => {
        try {
            setLoading(true);
            let data;
            
            if (userType === 'Job Seeker') {
                data = await getMyApplications();
            } else if (userType === 'Recruiter') {
                data = await getRecruiterApplications();
            }
            
            setApplications(data || []);
        } catch (error) {
            setError(error.error || 'Failed to load applications');
        } finally {
            setLoading(false);
        }
    }, [userType]);

    useEffect(() => {
        loadApplications();
        
        // Polling every 30 seconds for real-time updates
        const id = setInterval(loadApplications, 30000);

        // Cleanup polling on unmount
        return () => {
            if (id) clearInterval(id);
        };
    }, [loadApplications]);

    const handleStatusUpdate = async () => {
        if (!selectedApplication || !newStatus) return;

        try {
            setUpdating(true);
            const statusData = {
                status: newStatus,
                recruiterNotes: recruiterNotes.trim() || null
            };

            const updatedApp = await updateApplicationStatus(
                selectedApplication.id, 
                statusData
            );

            // Update the application in the list
            setApplications(prev => 
                prev.map(app => 
                    app.id === selectedApplication.id 
                        ? { ...app, ...updatedApp }
                        : app
                )
            );

            // Close modal and reset
            setShowStatusModal(false);
            setSelectedApplication(null);
            setNewStatus('');
            setRecruiterNotes('');
            
            setError(null);

        } catch (error) {
            setError(error.error || 'Failed to update status');
        } finally {
            setUpdating(false);
        }
    };

    const openStatusModal = (application) => {
        setSelectedApplication(application);
        setNewStatus(application.status);
        setRecruiterNotes(application.recruiterNotes || '');
        setShowStatusModal(true);
        setError(null);
    };

    const handleDownloadIndividual = async (applicationId, candidateName) => {
        try {
            const token = localStorage.getItem(process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token');
            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
            const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            
            const response = await fetch(`${cleanBaseUrl}/resume/download/${applicationId}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to download resume');
            }

            // Extract correct file extension from headers
            let extension = 'pdf';
            const contentDisposition = response.headers.get('content-disposition');
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    const filename = filenameMatch[1];
                    const lastDot = filename.lastIndexOf('.');
                    if (lastDot !== -1) {
                        extension = filename.substring(lastDot + 1);
                    }
                }
            } else {
                const contentType = response.headers.get('content-type');
                if (contentType) {
                    const ct = contentType.toLowerCase();
                    if (ct.includes('word') || ct.includes('msword') || ct.includes('officedocument.wordprocessingml.document')) {
                        extension = 'docx';
                    } else if (ct.includes('image/png')) {
                        extension = 'png';
                    } else if (ct.includes('image/jpeg') || ct.includes('image/jpg')) {
                        extension = 'jpg';
                    } else if (ct.includes('image/gif')) {
                        extension = 'gif';
                    }
                }
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${candidateName.replace(/\s+/g, '_')}_resume.${extension}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download error:', err);
            alert('Error downloading resume file: ' + err.message);
        }
    };

    // Clean up preview object URLs
    useEffect(() => {
        return () => {
            if (previewUrl && typeof previewUrl === 'string') {
                window.URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handleCloseModal = () => {
        if (previewUrl && typeof previewUrl === 'string') {
            window.URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        setPreviewType('');
        setPreviewLoading(false);
        setShowStatusModal(false);
    };

    const handleViewResume = async (applicationId, resumePath) => {
        if (!resumePath) return;

        const isWord = resumePath.toLowerCase().endsWith('.docx') || resumePath.toLowerCase().endsWith('.doc');
        if (isWord) {
            setPreviewType('word');
            setPreviewUrl('word-placeholder'); // Truthy value to show the warning panel
            return;
        }

        try {
            setPreviewLoading(true);
            setPreviewUrl(null);
            setError(null);

            const token = localStorage.getItem(process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token');
            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
            const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            
            const response = await fetch(`${cleanBaseUrl}/resume/download/${applicationId}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch resume file');
            }

            const blob = await response.blob();
            const isImage = resumePath.toLowerCase().endsWith('.png') || 
                            resumePath.toLowerCase().endsWith('.jpg') || 
                            resumePath.toLowerCase().endsWith('.jpeg') ||
                            blob.type.startsWith('image/');
                            
            const isPdf = resumePath.toLowerCase().endsWith('.pdf') || 
                          blob.type === 'application/pdf';

            if (isImage) {
                setPreviewType('image');
            } else if (isPdf) {
                setPreviewType('pdf');
            } else {
                setPreviewType('other');
            }

            const url = window.URL.createObjectURL(blob);
            setPreviewUrl(url);
        } catch (err) {
            console.error('Error viewing resume:', err);
            setError('Error loading resume preview: ' + err.message);
        } finally {
            setPreviewLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const variant = getStatusColor(status);

        return (
            <Badge 
                bg={variant} 
                className="d-inline-flex align-items-center px-2 py-1 border-0 shadow-sm"
                style={{ 
                    color: 'white',
                    fontSize: '0.75rem',
                    borderRadius: '6px'
                }}
            >
                <span className="me-1 d-flex align-items-center">
                    {getStatusIcon(status, 12)}
                </span>
                {getStatusLabel(status)}
            </Badge>
        );
    };

    const formatDate = (dateString) => {
        return sharedFormatDate(dateString);
    };

    const getNextStatuses = () => {
        if (!selectedApplication) return [];
        return getNextPossibleStatuses(selectedApplication.status);
    };

    if (loading) {
        return (
            <Card className="border-0 shadow-sm glass-card">
                <Card.Body className="text-center py-5">
                    <Loader2 size={40} className="text-primary spin-animation mb-3" />
                    <p className="text-muted fw-medium">Loading applications...</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <div className="application-status-manager">
            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-0 pt-4 px-4 pb-2">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold d-flex align-items-center">
                            <Briefcase size={20} className="me-2 text-primary" />
                            {userType === 'Job Seeker' ? 'My Applications' : 'Application Management'}
                        </h5>
                        <Button 
                            variant="light" 
                            size="sm" 
                            onClick={loadApplications}
                            className="d-flex align-items-center border shadow-sm px-3"
                        >
                            <RefreshCcw size={14} className="me-2" />
                            Refresh
                        </Button>
                    </div>
                </Card.Header>

                <Card.Body className="p-4 pt-2">
                    {error && (
                        <Alert variant="danger" className="border-0 shadow-sm d-flex align-items-center" dismissible onClose={() => setError(null)}>
                            <AlertTriangle size={18} className="me-2" />
                            {error}
                        </Alert>
                    )}

                    {applications.length === 0 ? (
                        <div className="text-center py-5 glass-card rounded-4">
                            <div className="text-muted mb-3 d-flex justify-content-center">
                                <Inbox size={64} strokeWidth={1} />
                            </div>
                            <p className="text-muted fw-medium mb-0">
                                {userType === 'Job Seeker' 
                                    ? 'You haven\'t applied for any jobs yet.' 
                                    : 'No applications received yet.'
                                }
                            </p>
                        </div>
                    ) : (
                        <Table responsive hover className="mb-0 custom-status-table">
                            <thead>
                                <tr>
                                    <th>Job Position</th>
                                    <th>Company</th>
                                    {userType === 'Recruiter' && <th>Applicant</th>}
                                    <th>Status</th>
                                    <th>Applied Date</th>
                                    <th>Last Updated</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {applications.map((app) => (
                                    <tr key={app.id}>
                                        <td className="py-3">
                                            <div className="fw-bold text-dark">{app.jobTitle}</div>
                                            {app.jobLocation && (
                                                <small className="text-muted d-flex align-items-center mt-1">
                                                    <MapPin size={12} className="me-1" />
                                                    {app.jobLocation}
                                                </small>
                                            )}
                                        </td>
                                        <td className="py-3 fw-medium">{app.companyName}</td>
                                        {userType === 'Recruiter' && (
                                            <td className="py-3">
                                                <div>
                                                    <div className="fw-bold">{app.applicantName}</div>
                                                    <small className="text-muted d-block">
                                                        {app.applicantEmail}
                                                    </small>
                                                </div>
                                            </td>
                                        )}
                                        <td className="py-3">{getStatusBadge(app.status)}</td>
                                        <td className="py-3 small text-muted">{formatDate(app.applyDate)}</td>
                                        <td className="py-3 small text-muted">{formatDate(app.lastUpdated)}</td>
                                        <td className="py-3 text-end">
                                            <Dropdown align="end">
                                                <Dropdown.Toggle 
                                                    variant="light" 
                                                    size="sm"
                                                    id={`dropdown-${app.id}`}
                                                    className="border-0 shadow-none p-2 rounded-circle hover-bg-light"
                                                >
                                                    <MoreVertical size={16} className="text-muted" />
                                                </Dropdown.Toggle>
                                                
                                                <Dropdown.Menu className="border-0 shadow-lg p-2 rounded-3" popperConfig={{ strategy: 'fixed' }}>
                                                    <Dropdown.Item onClick={() => openStatusModal(app)} className="rounded-2 d-flex align-items-center">
                                                        <Eye size={16} className="me-2 text-primary" />
                                                        View Details
                                                    </Dropdown.Item>
                                                    
                                                    {userType === 'Recruiter' && (
                                                        <Dropdown.Item onClick={() => openStatusModal(app)} className="rounded-2 d-flex align-items-center">
                                                            <Edit3 size={16} className="me-2 text-info" />
                                                            Update Status
                                                        </Dropdown.Item>
                                                    )}
                                                    
                                                    {userType === 'Job Seeker' && (
                                                        <>
                                                            {app.status === 'APPLIED' && (
                                                                <>
                                                                    <Dropdown.Divider />
                                                                    <Dropdown.Item 
                                                                        className="rounded-2 text-danger d-flex align-items-center"
                                                                        onClick={() => {
                                                                            setApplicationToWithdraw(app);
                                                                            setShowWithdrawModal(true);
                                                                        }}
                                                                    >
                                                                        <X size={16} className="me-2" />
                                                                        Withdraw
                                                                    </Dropdown.Item>
                                                                </>
                                                            )}
                                                            <Dropdown.Divider />
                                                            <Dropdown.Item 
                                                                className="rounded-2 text-danger d-flex align-items-center"
                                                                onClick={() => {
                                                                    setApplicationToDelete(app);
                                                                    setShowDeleteModal(true);
                                                                }}
                                                            >
                                                                <Trash2 size={16} className="me-2" />
                                                                Delete Application
                                                            </Dropdown.Item>
                                                        </>
                                                    )}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Status Update Modal */}
            <Modal show={showStatusModal} onHide={handleCloseModal} size="lg" centered>
                <Modal.Header closeButton className="border-0 px-4 pt-4">
                    <Modal.Title className="fw-bold d-flex align-items-center">
                        <Edit3 size={24} className="me-3 text-primary" />
                        Application Details
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="px-4">
                    {selectedApplication && (
                        <div className="p-2">
                            <Row className="g-3 mb-4">
                                <Col md={6}>
                                    <div className="p-3 bg-light rounded-3 h-100">
                                        <div className="small text-muted text-uppercase fw-bold mb-1">Job Position</div>
                                        <div className="fw-bold text-primary">{selectedApplication.jobTitle}</div>
                                        <div className="small fw-medium mt-1">{selectedApplication.companyName}</div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="p-3 bg-light rounded-3 h-100">
                                        <div className="small text-muted text-uppercase fw-bold mb-1">Status</div>
                                        <div className="mt-1">{getStatusBadge(selectedApplication.status)}</div>
                                        <div className="small text-muted mt-2">
                                            Applied: {formatDate(selectedApplication.applyDate)}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            
                            {userType === 'Recruiter' && (
                                <div className="applicant-info-panel p-3 border border-light rounded-3 mb-4 shadow-sm">
                                    <h6 className="fw-bold mb-3 d-flex align-items-center">
                                        <Info size={16} className="me-2 text-info" /> Applicant Information
                                    </h6>
                                    <Row>
                                        <Col md={6}>
                                            <div className="small text-muted">Name</div>
                                            <div className="fw-semibold">{selectedApplication.applicantName}</div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="small text-muted">Email</div>
                                            <div className="fw-semibold">{selectedApplication.applicantEmail}</div>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {userType === 'Recruiter' && selectedApplication.resumePath && (
                                <div className="mb-4">
                                    <label className="small fw-bold text-muted text-uppercase d-block mb-2">Resume / Documents</label>
                                    <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 border shadow-sm">
                                        <div className="d-flex align-items-center">
                                            <FileText size={24} className="text-danger me-2" />
                                            <div style={{ maxWidth: '280px' }}>
                                                <div className="fw-semibold text-dark text-truncate">
                                                    {selectedApplication.resumePath}
                                                </div>
                                                <small className="text-muted">Candidate uploaded resume</small>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm"
                                                className="d-flex align-items-center shadow-sm fw-semibold"
                                                onClick={() => handleViewResume(selectedApplication.id, selectedApplication.resumePath)}
                                            >
                                                <Eye size={14} className="me-2" />
                                                View
                                            </Button>
                                            <Button 
                                                variant="primary" 
                                                size="sm"
                                                className="d-flex align-items-center shadow-sm fw-semibold"
                                                onClick={() => handleDownloadIndividual(selectedApplication.id, selectedApplication.applicantName)}
                                            >
                                                <Upload size={14} className="me-2" style={{ transform: 'rotate(180deg)' }} />
                                                Download
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Resume Preview Pane */}
                                    {(previewLoading || previewUrl) && (
                                        <div className="mt-4 p-3 border rounded-3 bg-white shadow-sm">
                                            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                                <h6 className="fw-bold mb-0 d-flex align-items-center text-dark">
                                                    <FileText size={18} className="me-2 text-primary" />
                                                    Resume Preview
                                                </h6>
                                                <Button 
                                                    variant="light" 
                                                    size="sm" 
                                                    onClick={() => {
                                                        if (previewUrl && typeof previewUrl === 'string') {
                                                            window.URL.revokeObjectURL(previewUrl);
                                                        }
                                                        setPreviewUrl(null);
                                                        setPreviewType('');
                                                    }}
                                                    className="p-1 rounded-circle border-0"
                                                >
                                                    <X size={16} />
                                                </Button>
                                            </div>
                                            
                                            {previewLoading && (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" className="mb-2" />
                                                    <div className="text-muted small">Loading preview...</div>
                                                </div>
                                            )}
                                            
                                            {!previewLoading && previewType === 'word' && (
                                                <Alert variant="warning" className="mb-0 d-flex align-items-start border-0 shadow-sm rounded-3">
                                                    <Info size={20} className="me-3 mt-1 flex-shrink-0 text-warning" />
                                                    <div>
                                                        <div className="fw-bold text-dark">Microsoft Word Document (.doc/.docx)</div>
                                                        <div className="small mt-1 text-muted">
                                                            Inline preview is not supported for Word documents. Please click the <strong>Download</strong> button to view this file on your device.
                                                        </div>
                                                    </div>
                                                </Alert>
                                            )}

                                            {!previewLoading && previewType === 'pdf' && previewUrl && (
                                                <div className="ratio ratio-4x3" style={{ height: '480px' }}>
                                                    <iframe 
                                                        src={`${previewUrl}#toolbar=0`} 
                                                        title="Resume PDF Preview"
                                                        className="rounded-3 border shadow-sm w-100 h-100"
                                                    />
                                                </div>
                                            )}

                                            {!previewLoading && previewType === 'image' && previewUrl && (
                                                <div className="text-center p-2 border rounded-3 bg-light shadow-sm" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                                    <img 
                                                        src={previewUrl} 
                                                        alt="Candidate Resume Preview" 
                                                        className="img-fluid rounded-3" 
                                                        style={{ maxWidth: '100%', height: 'auto' }}
                                                    />
                                                </div>
                                            )}

                                            {!previewLoading && previewType === 'other' && previewUrl && (
                                                <Alert variant="info" className="mb-0 border-0 shadow-sm rounded-3">
                                                    <div className="fw-bold">Preview not available</div>
                                                    <div className="small mt-1">
                                                        This file format cannot be rendered inline. Please use the <strong>Download</strong> button to view the file.
                                                    </div>
                                                </Alert>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {userType === 'Recruiter' && (
                                <div className="status-form-panel">
                                    <Form.Group className="mb-4">
                                        <Form.Label className="small fw-bold text-muted text-uppercase">Update Status</Form.Label>
                                        <Form.Select
                                            value={newStatus}
                                            onChange={(e) => setNewStatus(e.target.value)}
                                            disabled={updating}
                                            className="border-light bg-light py-2"
                                        >
                                            <option value={selectedApplication.status}>
                                                {selectedApplication.status.replace('_', ' ')} (Current)
                                            </option>
                                            {getNextStatuses().map(status => (
                                                <option key={status} value={status}>
                                                    {status.replace('_', ' ')}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="small fw-bold text-muted text-uppercase">Recruiter Notes (Internal)</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={recruiterNotes}
                                            onChange={(e) => setRecruiterNotes(e.target.value)}
                                            placeholder="Add private notes about this application..."
                                            disabled={updating}
                                            className="border-light bg-light"
                                        />
                                    </Form.Group>
                                </div>
                            )}

                            {selectedApplication.coverLetter && (
                                <div className="mb-4">
                                    <label className="small fw-bold text-muted text-uppercase d-block mb-2">Cover Letter</label>
                                    <div className="bg-light p-3 rounded-3 border-start border-4 border-primary shadow-sm" style={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedApplication.coverLetter}
                                    </div>
                                </div>
                            )}

                            {userType === 'Job Seeker' && selectedApplication.recruiterNotes && (
                                <div className="mb-3">
                                    <label className="small fw-bold text-muted text-uppercase d-block mb-2">Message from Employer</label>
                                    <div className="bg-light p-3 rounded-3 border-start border-4 border-info shadow-sm">
                                        {selectedApplication.recruiterNotes}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                
                <Modal.Footer className="border-0 px-4 pb-4">
                    <Button 
                        variant="light" 
                        onClick={handleCloseModal}
                        disabled={updating}
                        className="px-4"
                    >
                        Close
                    </Button>
                    
                    {userType === 'Recruiter' && (
                        <Button 
                            variant="primary" 
                            onClick={handleStatusUpdate}
                            disabled={updating || !newStatus || newStatus === selectedApplication?.status}
                            className="px-4 d-flex align-items-center shadow"
                        >
                            {updating ? (
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                            ) : (
                                <CheckCircle2 size={18} className="me-2" />
                            )}
                            Update Status
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Withdraw Confirmation Modal */}
            <Modal show={showWithdrawModal} onHide={() => setShowWithdrawModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">Withdraw Application</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    <div className="bg-soft-danger p-3 rounded-circle d-inline-flex mb-3" style={{ backgroundColor: '#ffebee' }}>
                        <XCircle size={40} className="text-danger" />
                    </div>
                    <h5>Are you sure you want to withdraw?</h5>
                    <p className="text-muted px-3 mt-2">
                        You are about to withdraw your application for <br />
                        <strong className="text-dark">{applicationToWithdraw?.jobTitle}</strong>.
                    </p>
                    <div className="p-3 bg-light rounded-3 small text-muted text-start mt-3">
                        <strong>Important:</strong> This action cannot be undone and your application will be marked as Withdrawn.
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center gap-2 pb-4">
                    <Button variant="light" onClick={() => setShowWithdrawModal(false)} disabled={withdrawing} className="px-4">
                        Keep Application
                    </Button>
                    <Button variant="danger" onClick={handleWithdrawApplication} disabled={withdrawing} className="px-4 shadow">
                        {withdrawing ? 'Withdrawing...' : 'Confirm Withdrawal'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">Delete Application</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    <div className="bg-soft-danger p-3 rounded-circle d-inline-flex mb-3" style={{ backgroundColor: '#ffebee' }}>
                        <Trash2 size={40} className="text-danger" />
                    </div>
                    <h5>Are you sure you want to delete?</h5>
                    <p className="text-muted px-3 mt-2">
                        You are about to completely delete your application for <br />
                        <strong className="text-dark">{applicationToDelete?.jobTitle}</strong>.
                    </p>
                    <div className="p-3 bg-light rounded-3 small text-muted text-start mt-3">
                        <strong>Warning:</strong> This will completely remove your job application record from the database and the recruiter dashboard.
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center gap-2 pb-4">
                    <Button variant="light" onClick={() => setShowDeleteModal(false)} disabled={deleting} className="px-4">
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteApplication} disabled={deleting} className="px-4 shadow">
                        {deleting ? 'Deleting...' : 'Delete Permanently'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style jsx>{`
                .spin-animation {
                    animation: spin 2s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .custom-status-table thead th {
                    background-color: #f8f9fa;
                    border-bottom: 2px solid #edf2f7;
                    color: #718096;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                
                .hover-bg-light:hover {
                    background-color: #f8f9fa !important;
                }
            `}</style>
        </div>
    );
};

export default ApplicationStatusManager;